import React, { useEffect, useState } from "react";
import { IMySystemAccessRequestResponse } from "../../interfaces/user/UserInterfaces";
import SystemAccessRequestApi from "../../apis/systemAccessRequests/SystemAccessRequestApi";
import "./SystemAccessRequestStatus.scoped.scss";

interface SystemAccessRequestStatusProps {
  onSubmitNew?: () => void;
}

const SystemAccessRequestStatus: React.FC<SystemAccessRequestStatusProps> = ({ onSubmitNew }) => {
  const [requestData, setRequestData] = useState<IMySystemAccessRequestResponse[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await SystemAccessRequestApi.getMySystemAccessRequest();
        setRequestData(data);
      } catch {
        // Silently fail — user just won't see the status card
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const visibleRequests = requestData?.filter((request) => request.exists) ?? [];
  if (loading || visibleRequests.length === 0) {
    return null;
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <>
      {visibleRequests.map((request, index) => {
        const statusClass = request.status === "Approved"
          ? "status-approved"
          : request.status === "Rejected"
            ? "status-rejected"
            : "status-pending";

        const statusLabel = request.status === "New" ? "Pending" : request.status;
        const requestKey = request.id ?? request.createdOn ?? `${request.status ?? "request"}-${index}`;

        return (
          <div className="access-request-card" key={requestKey}>
            <h3>System Access Request</h3>
            <div className="status-row">
              <span className="label">Status:</span>
              <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
            </div>
            {request.roles && request.roles.length > 0 && (
              <div className="detail-row">
                <span className="label">Requested Roles:</span>
                <span className="value">{request.roles.join(", ")}</span>
              </div>
            )}
            {request.requestReason && (
              <div className="detail-row">
                <span className="label">Your Reason:</span>
                <span className="value">{request.requestReason}</span>
              </div>
            )}
            {request.createdOn && (
              <div className="detail-row">
                <span className="label">Submitted:</span>
                <span className="value">{formatDate(request.createdOn)}</span>
              </div>
            )}
            {request.decisionReason && (
              <div className="detail-row">
                <span className="label">Admin Response:</span>
                <span className="value">{request.decisionReason}</span>
              </div>
            )}
            {request.modifiedOn && request.status !== "New" && (
              <div className="detail-row">
                <span className="label">Decision Date:</span>
                <span className="value">{formatDate(request.modifiedOn)}</span>
              </div>
            )}
            {request.status === "Rejected" && onSubmitNew && (
              <div className="resubmit-row">
                <p className="resubmit-hint">Your request was rejected. You may review the admin's response and submit a new request with corrections.</p>
              </div>
            )}
            {request.status === "Approved" && onSubmitNew && (
              <div className="resubmit-row">
                <p className="resubmit-hint">Your roles may have been changed since this request was approved. You can submit a new access request if you need additional access.</p>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default SystemAccessRequestStatus;
